import React, { useState } from 'react';
import Button from './common/Button';
import appLogo from '../assets/Apps.svg';
import { useHistory } from 'react-router-dom';
import { sortFITypes, sortAccountsByName, groupAccountsBySuperType } from '../utils/sortingUtils';

const FiuConsentCard = ({
    consent,
    isSelected,
    onSelect,
    onViewDetails,
    customDetails,
    selectedAccounts,
}) => {
    const history = useHistory();
    const [expandedSections, setExpandedSections] = useState({});

    const isSectionExpanded = (accordionId, defaultOpen) =>
        expandedSections[accordionId] ?? defaultOpen;

    const toggleSection = (accordionId, defaultOpen) => {
        setExpandedSections((prev) => ({
            ...prev,
            [accordionId]: !isSectionExpanded(accordionId, defaultOpen),
        }));
    };

    const dateFormat = (dateString) => {
        if (dateString) {
            let newDate = new Date(dateString);
            let dataArray = newDate.toString().split(' ');
            return `${dataArray[2]} ${dataArray[1]} ${dataArray[3]} ${dataArray[4]}`;
        }
    };
    const handleViewDetails = () => {
        history.push({
            pathname: '/fiu-consent-details',
            state: {
                consent: consent,
                selectedAccounts: selectedAccounts,
            },
        });
    }

    const getStatusColor = (status) => {
        switch (status) {
            case 'ACTIVE':
                return '#DEFFE0';
            case 'PAUSED':
                return '#E2E2E2';
            default:
                return '#D9DDF';
        }
    };
    return (
        <div className="col-12 mb-2 mt-2">
            <style>{`
                .accordion-header:not(.collapsed) svg {
                    transform: rotate(180deg) !important;
                }
                .accordion-header svg {
                    transition: transform 0.3s ease;
                }
            `}</style>
            <div className="border border-1 border-black rounded-top-3" style={{ padding:'16px', borderRadius:'8px 8px 0 0' }}>
                <div className="d-flex align-items-center justify-content-between mb-2">
                     <div className="d-flex align-items-center" style={{ minHeight: '16px' }}>
                              <div 
                               className="d-inline-flex align-items-center justify-content-center rounded" 
                                  role="status"
                                  aria-label={`Consent status: ${consent?.status}`}
                                  style={{ 
                                   height: '16px',
                                   padding: '2px 6px',
                                   backgroundColor: getStatusColor(consent?.status),
                                   fontSize: '10px',
                                   fontWeight: '600',
                                   lineHeight: '12px',
                                   color: consent?.status === 'ACTIVE' ? '#006607' : 
                                          consent?.status === 'PAUSED' ? '#A1A1A1' : '#111111'
                                  }}
                              >
                                 {consent?.status}
                            </div>
                     </div>
                    <div className="d-flex align-items-center">
                       <button
                         type="button"
                         className="card-action-btn"
                         role="checkbox"
                         aria-checked={isSelected}
                         aria-label={`Select consent for ${consent?.consentPurposeText}`}
                         onClick={() => onSelect(consent)}
                       >
                            <div
                                 style={{
                                     width: '16px',
                                     height: '16px',
                                     borderRadius: '4px',
                                     display: 'flex',
                                     alignItems: 'center',
                                     justifyContent: 'center',
                                     backgroundColor: isSelected ? (customDetails?.primaryBtnColor || '#273C89') : 'white',
                                     border: isSelected ? '2px solid #273C89' : '2px solid #6B6D6F',
                                 }}
                             >
                                 {isSelected && (
                                     <svg width="12" height="10" viewBox="0 0 12 10" fill="none" aria-hidden="true">
                                         <path d="M1 5L4.5 8.5L11 1.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                     </svg>
                                 )}
                             </div>
                       </button>
                     </div>
                </div>
                <div className="d-flex align-items-center justify-content-between mb-3">
                     <div className="d-flex align-items-center">
                          <h2 className="fs-14 fw-500 text-black text-start mb-0" style={{ color: '#111111' }}>
                              {consent?.consentPurposeText}
                          </h2>
                     </div>
                </div>
                
                {/* Accounts grouped by SuperType in accordions */}
                {consent?.consentDetails?.Accounts && consent.consentDetails.Accounts.length > 0 && (() => {
                // Group accounts by SuperType
                const accountsBySuperType = groupAccountsBySuperType(consent.consentDetails.Accounts);

                const superTypes = sortFITypes(Object.keys(accountsBySuperType));
                return superTypes.map((superType, index) => {
                    const accounts = sortAccountsByName(accountsBySuperType[superType]);
                    const accordionId = `accordion-${consent.consentIntentId}-${superType.replace(/\s+/g, '-')}-${index}`;
                    const defaultOpen = index === 0;
                    const isExpanded = isSectionExpanded(accordionId, defaultOpen);
                    
                    return (
                        <div 
                            key={superType} 
                            className="mb-2"
                            style={{ 
                                border: '1px solid #E8E8E8',
                                borderRadius: '8px',
                                overflow: 'hidden'
                            }}
                        >
                            <button
                                type="button"
                                className={`accordion-header d-flex align-items-center justify-content-between w-100 border-0 ${isExpanded ? '' : 'collapsed'}`}
                                style={{ 
                                    height: '30px',
                                    padding: '0 16px',
                                    backgroundColor: 'transparent',
                                    cursor: 'pointer'
                                }}
                                aria-expanded={isExpanded}
                                aria-controls={accordionId}
                                aria-labelledby={`${accordionId}-title`}
                                onClick={() => toggleSection(accordionId, defaultOpen)}
                            >
                                <h3 id={`${accordionId}-title`} className="fs-12 fw-600 mb-0" style={{ color: '#111111' }}>
                                    {accounts.length} {superType} Account(s)
                                </h3>
                                <svg 
                                    width="14" 
                                    height="14" 
                                    viewBox="0 0 14 14" 
                                    fill="none"
                                    aria-hidden="true"
                                >
                                    <path 
                                        d="M4.08 5.25L7 8.166L9.913 5.25H4.08Z" 
                                        fill="#1D1B20"
                                    />
                                </svg>
                            </button>
                            {isExpanded && (
                            // No role="region" here: the button's aria-expanded/aria-controls
                            // already exposes this as a disclosure panel. Marking every
                            // per-account-type panel as a landmark region caused duplicate,
                            // indistinguishable "region" landmarks whenever two consent cards
                            // had the same FI super-type (e.g. two "Deposits" sections),
                            // which axe flags as landmark-unique violations.
                            <div id={accordionId} style={{ padding: '12px 16px 16px' }}>
                                <ul
                                    className="list-unstyled m-0"
                                    role="list"
                                    style={{ backgroundColor: '#FFFFFF', display: 'flex', flexWrap: 'wrap', gap: '10px' }}
                                >
                                    {accounts.map((account, accIndex) => {
                                        const last4 = account.maskedAccNumber?.slice(-4) || account.linkRefNumber?.slice(-4) || '****';
                                        return (
                                            <li
                                                key={accIndex}
                                                role="listitem"
                                                aria-label={`${account.fipName || account.fipId}, account ending ${last4}`}
                                                className="d-flex align-items-center"
                                                style={{
                                                    width: '130px',
                                                    height: '26px',
                                                    padding: '0 8px',
                                                    backgroundColor: '#F5F5F5',
                                                    borderRadius: '24px',
                                                    gap: '6px',
                                                    overflow: 'hidden',
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <img
                                                    src={account.fipLogoUri || appLogo}
                                                    alt=""
                                                    aria-hidden="true"
                                                    style={{ width: '16px', height: '16px', objectFit: 'contain', flexShrink: 0 }}
                                                />
                                                <span
                                                    aria-hidden="true"
                                                    className="fs-12 fw-500"
                                                    style={{
                                                        color: '#6C6C6C',
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        flex: 1,
                                                        minWidth: 0
                                                    }}
                                                >
                                                    {account.fipName || account.fipId}
                                                </span>
                                                <span aria-hidden="true" className="fs-12 fw-400" style={{ color: '#6C6C6C', flexShrink: 0 }}>
                                                    • {last4}
                                                </span>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                            )}
                        </div>
                    );
                });
            })()}
            </div>
            
            <div className="d-flex align-items-center justify-content-between border-start border-end border-bottom rounded-bottom-3" style={{ backgroundColor: '#F6F6F6', padding:'0 14px', borderRadius:'0 0 8px 8px', borderColor: '#D9D9D9' }}>
                <p className="fs-12 fw-400 text-start mb-0" style={{ color: '#6C6C6C', padding:'14px 0', borderRadius:'8px' }}>
                    Issued on {dateFormat(consent?.consentIntentUpdateTimestamp)}
                </p>
                <div className="col-5 d-flex justify-content-end p-0">
                    <Button
                        className="fs-14 fw-600 bg-transparent border-0 rounded-3 p-0"
                        style={{ color: '#273C89', textDecoration:'underline' }}
                        onClick={handleViewDetails}
                        aria-label={`View details for ${consent?.consentPurposeText}`}
                    >
                        View Details
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default FiuConsentCard;

